import axios from 'axios';

/**
 * Extracts the filename the server chose from a Content-Disposition header.
 *
 * Prefers the RFC 5987 `filename*=utf-8''<pct-encoded>` parameter (which carries the original,
 * possibly non-ASCII name), and falls back to the plain `filename="..."`/`filename=...` parameter.
 * Values that look like an RFC 2047 encoded-word (`=?...?=`) are rejected — some header writers
 * MIME-encode non-ASCII names into base64 gibberish that must never be used as a filename.
 *
 * Returns null when the header is absent or no usable filename could be found, so callers can
 * apply their own default.
 */
export function getFilenameFromContentDisposition(header: string | undefined | null): string | null {
	if (!header) {
		return null;
	}

	// RFC 5987 extended parameter: filename*=utf-8''some%20name.csv (charset case-insensitive,
	// optional language tag between the quotes).
	const extendedMatch = header.match(/filename\*\s*=\s*utf-8'[^']*'([^;]+)/i);
	if (extendedMatch && extendedMatch[1]) {
		const value = extendedMatch[1].trim();
		try {
			return decodeURIComponent(value);
		} catch (e) {
			// Malformed percent-encoding — fall through to the plain filename parameter.
		}
	}

	// Plain parameter: filename="name.csv" or filename=name.csv. The (?!\*) guard stops this from
	// matching the filename*= parameter handled above.
	const plainMatch = header.match(/filename(?!\*)\s*=\s*(?:"([^"]*)"|([^;]+))/i);
	if (plainMatch) {
		const value = (plainMatch[1] ?? plainMatch[2] ?? '').trim();
		if (!value || isRfc2047EncodedWord(value)) {
			return null;
		}
		return value;
	}

	return null;
}

/**
 * Detects an RFC 2047 encoded-word, e.g. =?utf-8?B?WsO8cmljaC5jc3Y=?= — the MIME encoding some
 * header writers apply to non-ASCII values, which is meaningless as a filename.
 */
function isRfc2047EncodedWord(value: string): boolean {
	return /^=\?[^?]+\?[bq]\?[^?]*\?=$/i.test(value);
}

/**
 * The message the server sent with a failed download, or an empty string when it sent nothing usable.
 *
 * Needed because a download asks for `responseType: 'blob'`, which applies to error responses too — so a
 * 400's `ProblemDetails` or plain-text reason arrives as a Blob rather than a string, and reading
 * `error.response.data` directly yields `[object Blob]`. Without this the only thing the screen could say
 * was a generic "try again", which is the wrong advice for every case the server actually explains.
 */
export async function readDownloadErrorMessage(error: unknown): Promise<string> {
	const data = (error as { response?: { data?: unknown } })?.response?.data;

	if (!(data instanceof Blob)) {
		return '';
	}

	try {
		const text = (await data.text()).trim();
		if (!text) {
			return '';
		}

		// ASP.NET answers some failures with ProblemDetails JSON and others with a bare string.
		if (text.startsWith('{')) {
			const problem = JSON.parse(text) as { detail?: string; title?: string };
			return problem.detail ?? problem.title ?? '';
		}

		// Long bodies are an HTML error page or a stack trace, not something to put in a toast.
		return text.length > 300 ? '' : text;
	} catch (e) {
		return '';
	}
}

/**
 * GETs a URL as a file and hands it to the browser to save, naming it from the response's
 * Content-Disposition where the server set one.
 *
 * Throws an Error carrying the server's own message when there is one, so a caller's toast can say why
 * rather than only that something went wrong.
 *
 * Note this is not yet used by the older exports in `ReportService` — there are several hand-rolled
 * copies of the anchor-click dance in there that predate it. New download paths should use this.
 */
export async function downloadFileFromUrl(
	url: string,
	fallbackFileName: string,
	params?: URLSearchParams,
): Promise<void> {
	let response;
	try {
		response = await axios.get(url, { params, responseType: 'blob' });
	} catch (error) {
		throw new Error(await readDownloadErrorMessage(error));
	}

	const fileName = getFilenameFromContentDisposition(response.headers['content-disposition']) ?? fallbackFileName;

	const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
	const link = document.createElement('a');
	link.href = blobUrl;
	link.setAttribute('download', fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(blobUrl);
}

/**
 * Wraps CSV text the browser has built into a downloadable blob, with a UTF-8 byte-order mark.
 *
 * A CSV carries no declaration of its own encoding, so Excel guesses — and on Windows it guesses
 * the machine's ANSI code page rather than UTF-8 unless the file opens with this mark. Without it
 * every non-ASCII character is mangled: the prime and double-prime in a vehicle length like 12′ 6″
 * arrive as "12â€² 6â€³", along with accented names and curly apostrophes. Everything else that
 * reads CSV skips the mark.
 *
 * Only for CSV text assembled here or returned as a string. A response fetched with
 * `responseType: 'blob'` is already the file the server encoded — re-wrapping that with another
 * mark gives it two, and Excel shows the second one as "ï»¿" in the first cell.
 */
export function csvBlobFromText(csv: string): Blob {
	return new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
}
